import { forwardRef, ReactElement, RefObject, useState } from 'react';
import { DividerProps, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Box, Drawer, DrawerProps, List, Paper } from '@mui/material';
import ExpandableMenuItem from '@/layouts/ManagerLayout/ExpandableMenuItem';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSpaceUserV2Store } from '@/store';

interface MenuItemBase {
    id?: string | number;
    label: string;
    icon?: ReactElement;
    to?: string;
    href?: string;
    target?: string;
    action?: () => void;
    isExistMatch?: boolean;
}

export interface MenuItem extends MenuItemBase {
    children?: MenuItemBase[] | ReactElement;
    isShowDivider?: boolean;
    DividerProps?: DividerProps;
    isActive?: boolean;
}

interface Props extends DrawerProps {
    isDrawerOpen?: boolean;
    setIsDrawerOpen?: (open: boolean) => void;
    appBarHeight?: number;
    menu?: MenuItem[];
    append?: ReactElement;
}

const MenuDrawer = (props: Props, ref: any) => {
    const { t } = useTranslation();
    const { isDrawerOpen = true, setIsDrawerOpen, appBarHeight = 64, menu = [], append } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { grade_name } = useSpaceUserV2Store();
    const isSodomall = grade_name === '소도몰';

    return (
        <Box sx={{ position: 'relative' }}>
            <Drawer
                PaperProps={{
                    ref: ref,
                    sx: {
                        minWidth: 240,
                        maxWidth: 320,
                        marginTop: `${appBarHeight}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        '&::-webkit-scrollbar': {
                            // display:'none',
                            // backgroundColor: 'red',
                            // width: 30,
                        },
                    },
                }}
                variant={!isMobile ? 'persistent' : 'temporary'}
                open={isDrawerOpen}
                sx={{
                    flexShrink: 0,
                }}
            >
                <Paper elevation={0}>
                    <List
                        sx={{
                            '& .MuiListItemIcon-root': { minWidth: 35 },
                        }}
                    >
                        {menu.map((item, index) => {
                            return <ExpandableMenuItem {...item} key={item?.id || index} />;
                        })}
                    </List>
                    <List
                        sx={{
                            '& .MuiListItemIcon-root': { minWidth: 35 },
                        }}
                    >
                        {/* 고객센터 - 소도몰 역할일 때 숨김 */}
                        {!isSodomall && (
                            <ExpandableMenuItem
                                label={t('product.customerCenter')}
                                target="_blank"
                                href="http://pf.kakao.com/_BxfaRn/chat"
                                // href='https://help.flowing.space/hc/ko/requests/new'
                                isShowDivider
                                icon={<SupportAgentIcon />}
                            />
                        )}
                        {/* 플로잉 업데이트 - 소도몰 역할일 때 숨김 */}
                        {!isSodomall && (
                            <ExpandableMenuItem
                                label={t('product.ddmLiveUpdate')}
                                href="https://flowing.oopy.io/0a190ec2-b0b8-435d-9701-9488f8be7d0a"
                                // label="사용 가이드"
                                // href='https://flowing.oopy.io/guide/seller/profile-link'
                                target="_blank"
                                // isShowDivider
                                icon={<ArticleOutlinedIcon />}
                            />
                        )}
                    </List>
                </Paper>
                {!!append ? (
                    <div
                        style={{
                            paddingBottom: appBarHeight,
                        }}
                    >
                        {append}
                    </div>
                ) : (
                    ''
                )}
            </Drawer>

            {/* 사이드바 토글 버튼 - 사이드바 우측 경계선에 붙임 */}
            {setIsDrawerOpen && !isMobile && (
                <IconButton
                    onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                    sx={{
                        position: 'fixed',
                        left: isDrawerOpen ? '240px' : '0px',
                        top: '50%',
                        transform: isDrawerOpen ? 'translateY(-50%) translateX(-50%)' : 'translateY(-50%)',
                        zIndex: 'calc( var(--v-zIndex-drawer) + 2)',
                        backgroundColor: isDrawerOpen ? '#fff' : 'primary.main',
                        border: isDrawerOpen ? '1px solid' : '2px solid',
                        borderColor: isDrawerOpen ? 'divider' : 'primary.dark',
                        borderLeft: 'none',
                        boxShadow: isDrawerOpen ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '2px 2px 12px rgba(0, 0, 0, 0.3)',
                        width: '28px',
                        height: '48px',
                        padding: '4px',
                        borderRadius: '0 8px 8px 0',
                        transition: 'all 225ms cubic-bezier(0, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: isDrawerOpen ? 'primary.light' : 'primary.dark',
                            borderColor: 'primary.dark',
                            boxShadow: isDrawerOpen ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '2px 2px 16px rgba(0, 0, 0, 0.4)',
                            transform: isDrawerOpen ? 'translateY(-50%) translateX(-50%)' : 'translateY(-50%) translateX(2px)',
                            '& .MuiSvgIcon-root': {
                                color: isDrawerOpen ? 'primary.dark' : '#fff',
                            },
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                            color: isDrawerOpen ? 'text.secondary' : '#fff',
                            transition: 'color 0.2s',
                        },
                    }}
                    aria-label={isDrawerOpen ? '사이드바 닫기' : '사이드바 열기'}
                    title={isDrawerOpen ? '사이드바 닫기' : '사이드바 열기'}
                >
                    {isDrawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
            )}
        </Box>
    );
};

export default forwardRef(MenuDrawer);
